import React, { useEffect, useState, useRef } from "react";
import { Outlet, useNavigate } from "react-router-dom";
import { ApolloError } from "@apollo/client";
import { TabsWithNavigation } from "@/components/TabsWithNavigation";
import { useSendBulkMessage } from "./useSendBulkMessage";
import { FormProvider, useForm } from "react-hook-form";
import { FormValues, SubmissionReturn } from "./types";
import { MessagingYesNoEnum, RecoverableError } from "@/__generated__/graphql";
import { BulkMessageCongrats } from "./BulkMessageCongrats";
import { toast } from "sonner";
import { i18n } from "@/i18n";
import { useNow } from "@/hooks/useNow";
import { addMinutes } from "date-fns";
import { ROUTES } from "@/routes/paths";
import { useOrgTimeZone } from "@/hooks/useOrgTimeZone";
import { useProtectedNavigation } from "@/routes/useProtectedNavigation";
import { filterEmptyMessageParts } from "./utils/mutationBuilders";
import { useRoute } from "@/routes/getRoute";
import { useUrlState } from "@/hooks/useUrlSerializer";
import { UnitEnum } from "@/__generated__/types";

const defaultValues: FormValues = {
  name: "",
  filters: {
    userIds: [],
    assignedCounselors: [],
    commitIndex: [],
    firstTimeMessageRecipients: MessagingYesNoEnum.No,
    funnelStates: [],
    interests: [],
    location: [],
    majors: [],
    startTerm: [],
    studentSource: [],
    studentType: [],
  },
  messageParts: [],
};

export interface BulkMessageOutletContext {
  markTabCompleted: (tabId: string) => void;
  markTabIncomplete: (tabId: string) => void;
  isTabCompleted: (tabId: string) => boolean;
  setHasUnsavedChanges: React.Dispatch<React.SetStateAction<boolean>>;
  currentTabId: string;
  setLoading: React.Dispatch<React.SetStateAction<boolean>>;
  isLoading: boolean;
  setCsvFile: React.Dispatch<React.SetStateAction<File | undefined>>;
  csvFile: File | undefined;
  setStudentCount: React.Dispatch<React.SetStateAction<number | undefined>>;
  isActive: boolean;
  timeZone?: string;
}

interface SendBulkMessageProps {
  submissionData: SubmissionReturn;
  loading: boolean;
  isActive: boolean;
  formValues?: FormValues;
  askSaveDraft: boolean;
  submitHandler: (values: FormValues, options: { draft: boolean }) => void;
  error?: ApolloError;
  bulkMessageId?: string;
}

export function SendBulkMessage({
  submissionData,
  loading,
  isActive,
  formValues,
  askSaveDraft,
  submitHandler,
  error,
  bulkMessageId,
}: SendBulkMessageProps) {
  const { riseActionId } = useUrlState("BULK_MESSAGE_COMPOSE") || {};

  if (riseActionId) {
    defaultValues["sendOption"] = { sendNow: UnitEnum.Unit };
    defaultValues["filters"]["riseActionId"] = riseActionId;
    defaultValues["filters"]["firstTimeMessageRecipients"] = undefined;
  }

  const [isLoading, setLoading] = useState<boolean>(false);
  const [csvFile, setCsvFile] = useState<File>();
  const [studentCount, setStudentCount] = useState<number>(0);
  const lastProcessedErrorRef = useRef<ApolloError | null>(null);
  const form = useForm<FormValues>({
    defaultValues: Object.assign({ ...defaultValues }, formValues || {}),
  });
  const { handleSubmit, formState } = form;
  const { navToRoute } = useProtectedNavigation();
  const {
    timeZone,
    loading: loadingTimeZone,
    error: timeZoneError,
  } = useOrgTimeZone();
  const navigate = useNavigate();
  const getRoute = useRoute();

  const now = useNow();

  const tabNavigationProps = useSendBulkMessage({
    completionMode: "content", // Use content-controlled completion
    bulkMessageId,
    initialCompletedTabs: isActive
      ? [ROUTES.COMMUNITY_TOOLS.BULK_MESSAGING.BASIC_INFORMATION]
      : [],
  });

  const { markTabCompleted, markTabIncomplete, currentTabId } =
    tabNavigationProps;

  // Context to pass to child routes
  const outletContext = {
    markTabCompleted,
    markTabIncomplete,
    isTabCompleted: tabNavigationProps.isTabCompleted,
    setHasUnsavedChanges: tabNavigationProps.setHasUnsavedChanges,
    currentTabId,
    setLoading,
    isLoading,
    csvFile,
    setCsvFile,
    setStudentCount,
    isActive,
    timeZone,
  } as BulkMessageOutletContext;

  if (timeZoneError && !timeZone) {
    toast.error(i18n.t("bulkMessaging.timeZoneError"));
    navToRoute("bulkMessaging");
  }

  const editRoute = getRoute("bulkMessaging");

  useEffect(() => {
    if (submissionData.result?.success && submissionData.draft) {
      markTabCompleted(currentTabId);
      if (submissionData.draft) {
        draftSuccessToast();
        navigate(editRoute);
      }
    } else if (submissionData.result && !submissionData.result.success) {
      errorToast(submissionData.result?.errors);
    }
  }, [
    submissionData.id,
    submissionData.result,
    submissionData.draft,
    markTabCompleted,
    currentTabId,
    navigate,
    editRoute,
  ]);

  useEffect(() => {
    if (error && error !== lastProcessedErrorRef.current) {
      lastProcessedErrorRef.current = error;
      errorToast();
    }
  }, [error]);

  const onTabChange = (id: string, changeTab: () => void) => {
    const handleSuccess = () => {
      markTabCompleted(currentTabId);
      changeTab();
    };

    const handleError = () => {
      markTabIncomplete(currentTabId);
    };
    handleSubmit(handleSuccess, handleError)();
  };

  const onSubmit = () => {
    const handleSuccess = (values: FormValues) => {
      if (!validateSubmission(values, now, studentCount)) {
        return;
      }

      submitHandler(values, { draft: false });
    };
    handleSubmit(handleSuccess)();
  };

  const onSaveDraft = () => {
    const values = form.getValues();

    if (!values.name?.length) {
      return toast.error(
        i18n.t("bulkMessaging.basicInformation.nameRequiredError"),
      );
    }

    submitHandler(values, { draft: true });
  };

  return (
    <TabsWithNavigation {...tabNavigationProps} onTabChange={onTabChange}>
      {submissionData.result?.success && !submissionData.draft ? (
        <BulkMessageCongrats
          students={submissionData.audienceReach.students}
          totalCount={submissionData.audienceReach.totalCount}
        />
      ) : (
        <TabsWithNavigation.Body
          routes={tabNavigationProps.routes}
          currentTabId={tabNavigationProps.currentTabId}
          onTabChange={onTabChange}
          onSaveDraft={onSaveDraft}
          onSubmit={onSubmit}
          showSaveDraft={true}
          askSaveDraft={askSaveDraft}
          isSubmitDisabled={
            loading ||
            isLoading ||
            loadingTimeZone ||
            !!Object.keys(formState.errors).length
          }
          isLoading={
            loading ||
            isLoading ||
            loadingTimeZone ||
            !!Object.keys(formState.errors).length
          }
        >
          <FormProvider {...form}>
            <form className="max-w-225 min-w-185">
              <Outlet context={outletContext} />
            </form>
          </FormProvider>
        </TabsWithNavigation.Body>
      )}
    </TabsWithNavigation>
  );
}

function errorToast(errors?: RecoverableError[]) {
  toast.error(i18n.t("bulkMessaging.navigation.submitError.title"), {
    description:
      errors?.map(({ message }) => message).join(", ") ||
      i18n.t("bulkMessaging.navigation.submitError.description"),
  });
}

function validateSubmission(
  values: FormValues,
  now: number,
  studentCount: number,
) {
  const startSendingAt =
    values.sendOption?.sendLater?.startSendingAt ||
    values.sendOption?.startCampaign?.startSendingAt;
  if (startSendingAt && startSendingAt <= addMinutes(new Date(now), 1)) {
    toast.error(i18n.t("bulkMessaging.review.timingError"));
    return false;
  }

  if (values.sendOption?.sendNow && studentCount === 0) {
    toast.error(i18n.t("bulkMessaging.review.studentCountError"));
    return false;
  }

  if (!filterEmptyMessageParts(values.messageParts).length) {
    toast.error(i18n.t("bulkMessaging.review.messageError"));
    return false;
  }

  return true;
}

function draftSuccessToast() {
  toast(i18n.t("bulkMessaging.navigation.draftSuccess.title"), {
    description: i18n.t("bulkMessaging.navigation.draftSuccess.description"),
  });
}
